import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getIdToken, signOut } from "@/lib/firebase";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { australianStates } from "@shared/regions";
import { Article, categories } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  Save,
  ShieldCheck,
  Sparkles,
  Upload,
  WandSparkles,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useRoute } from "wouter";
import { z } from "zod";

const formSchema = z.object({
  title: z.string(),
  titleKo: z.string(),
  content: z.string(),
  contentKo: z.string(),
  summary: z.string(),
  summaryKo: z.string(),
  author: z.string(),
  imageUrl: z.string(),
  sourceUrl: z.string(),
  isBreaking: z.boolean(),
  status: z.enum(["draft", "pending", "published"]).default("draft"),
  sourceName: z.string().optional(),
  imageCredit: z.string().optional(),
  publishedAt: z.string().optional(),
  tags: z.union([z.string(), z.array(z.string())]),
  imageDescription: z.string().optional(),
  keyHighlight: z.string().optional(),
  category: z.string(),
  region: z.string(),
}).superRefine((value, context) => {
  if (!value.title.trim() && !value.titleKo.trim()) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["titleKo"],
      message: "한글 또는 영문 제목을 입력해 주세요.",
    });
  }
});

interface AiAssistResult {
  title: string;
  titleKo: string;
  content: string;
  contentKo: string;
  summary: string;
  summaryKo: string;
  keyHighlight: string;
  factsToVerify: string[];
  changeSummary: string[];
}

interface ImageResult {
  url: string;
  credit: string;
  description?: string;
}

export default function AdminNewArticle() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [editMatch, editParams] = useRoute<{ id: string }>("/admin/edit/:id");
  const articleId = editMatch && editParams ? editParams.id : undefined;
  const isEditMode = Boolean(articleId);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [aiMode, setAiMode] = useState<"polish" | "structure" | "headline">("polish");
  const [sourceNotes, setSourceNotes] = useState("");
  const [aiInstructions, setAiInstructions] = useState("");
  const [factsToVerify, setFactsToVerify] = useState<string[]>([]);
  const [changeSummary, setChangeSummary] = useState<string[]>([]);
  const [imagePrompt, setImagePrompt] = useState("");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      titleKo: "",
      content: "",
      contentKo: "",
      summary: "",
      summaryKo: "",
      category: "international" as const,
      status: "draft" as const,
      region: "ALL" as string,
      tags: [] as string[],
      isBreaking: false,
      imageUrl: "",
      imageDescription: "",
      keyHighlight: "",
      sourceUrl: "",
      sourceName: "",
      imageCredit: "",
      author: "",
      publishedAt: new Date().toISOString().split("T")[0],
    },
  });

  const { data: article, isLoading: articleLoading } = useQuery<Article>({
    queryKey: [`/api/admin/articles/${articleId}`],
    enabled: isEditMode && !!articleId,
    queryFn: async () => {
      const token = await getIdToken();
      const response = await fetch(`/api/admin/articles/${articleId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!response.ok) throw new Error(`Failed to load article: ${response.status}`);
      return response.json();
    },
  });

  useEffect(() => {
    if (article && isEditMode) {
      // Parse tags from JSON string or array
      let tagsValue = "";
      if (Array.isArray(article.tags)) {
        tagsValue = article.tags.join(", ");
      } else if (typeof article.tags === "string") {
        try {
          const parsed = JSON.parse(article.tags);
          tagsValue = Array.isArray(parsed) ? parsed.join(", ") : article.tags;
        } catch {
          tagsValue = article.tags;
        }
      }

      form.reset({
        title: article.title || "",
        titleKo: article.titleKo || "",
        content: article.content || "",
        contentKo: article.contentKo || "",
        summary: article.summary || "",
        summaryKo: article.summaryKo || "",
        category: article.category as any,
        region: (article as any).region || "ALL",
        tags: tagsValue as any,
        isBreaking: article.isBreaking || false,
        imageUrl: article.imageUrl || "",
        imageDescription: article.imageDescription || "",
        keyHighlight: article.keyHighlight || "",
        sourceUrl: article.sourceUrl || "",
        sourceName: (article as any).sourceName || "",
        imageCredit: (article as any).imageCredit || "",
        status: (["draft", "pending", "published"] as const).includes(article.status as any)
          ? (article.status as "draft" | "pending" | "published")
          : "draft",
        author: article.author || "",
        publishedAt: article.publishedAt
          ? new Date(article.publishedAt).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    }
  }, [article, isEditMode, form]);

  useEffect(() => {
    if (!authLoading && !user) {
      setLocation("/admin");
    }
  }, [authLoading, user, setLocation]);

  const createMutation = useMutation({
    mutationFn: (data: z.infer<typeof formSchema>) =>
      apiRequest("POST", "/api/articles", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      toast({
        title: "생성 완료",
        description: "새 기사가 생성되었습니다.",
      });
      setLocation("/admin/articles");
    },
    onError: () => {
      toast({
        title: "오류",
        description: "기사 생성에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const token = await getIdToken();
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`/api/articles/${articleId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
      }
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/articles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/articles", articleId] });
      toast({
        title: "수정 완료",
        description: "기사가 수정되었습니다.",
      });
      setLocation("/admin/articles");
    },
    onError: (error: Error) => {
      console.error("[AdminNewArticle] Update error:", error);
      toast({
        title: "오류",
        description: error.message || "기사 수정에 실패했습니다.",
        variant: "destructive",
      });
    },
  });

  const aiAssistMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/articles/assist", {
        mode: aiMode,
        ...form.getValues(),
        sourceNotes,
        instructions: aiInstructions,
      });
      return (await response.json() as { data: AiAssistResult }).data;
    },
    onSuccess: (result) => {
      (["title", "titleKo", "content", "contentKo", "summary", "summaryKo", "keyHighlight"] as const)
        .forEach((field) => {
          if (result[field]) form.setValue(field, result[field], { shouldDirty: true, shouldValidate: true });
        });
      setFactsToVerify(result.factsToVerify || []);
      setChangeSummary(result.changeSummary || []);
      toast({ title: "AI 편집 완료", description: "결과를 반영했습니다. 사실 확인 항목을 검토해 주세요." });
    },
    onError: (error: Error) => {
      toast({ title: "AI 편집 실패", description: error.message, variant: "destructive" });
    },
  });

  const imageUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await apiRequest("POST", "/api/admin/articles/image-upload", formData);
      return (await response.json() as { data: ImageResult }).data;
    },
    onSuccess: (result) => {
      form.setValue("imageUrl", result.url, { shouldDirty: true, shouldValidate: true });
      form.setValue("imageCredit", result.credit, { shouldDirty: true });
      toast({ title: "이미지 업로드 완료", description: "대표 이미지와 크레딧을 반영했습니다." });
    },
    onError: (error: Error) => {
      toast({ title: "이미지 업로드 실패", description: error.message, variant: "destructive" });
    },
  });

  const imageGenerateMutation = useMutation({
    mutationFn: async () => {
      const values = form.getValues();
      const response = await apiRequest("POST", "/api/admin/articles/image-generate", {
        prompt: imagePrompt,
        title: values.titleKo || values.title,
        context: (values.summaryKo || values.contentKo || values.summary || values.content).slice(0, 1_500),
      });
      return (await response.json() as { data: ImageResult }).data;
    },
    onSuccess: (result) => {
      form.setValue("imageUrl", result.url, { shouldDirty: true, shouldValidate: true });
      form.setValue("imageCredit", result.credit, { shouldDirty: true });
      if (result.description) form.setValue("imageDescription", result.description, { shouldDirty: true });
      toast({ title: "AI 이미지 생성 완료", description: "대표 이미지와 AI 생성 크레딧을 반영했습니다." });
    },
    onError: (error: Error) => {
      toast({ title: "AI 이미지 생성 실패", description: error.message, variant: "destructive" });
    },
  });

  if (authLoading || (isEditMode && articleLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Admin access check
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>접근 권한 없음</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              이 페이지는 관리자만 접근할 수 있습니다.
            </p>
            <div className="flex gap-2">
              <Link href="/">
                <Button data-testid="button-go-home">홈으로 이동</Button>
              </Link>
              <Button
                variant="outline"
                onClick={() => signOut()}
                data-testid="button-logout"
              >
                로그아웃
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    // 교민뉴스 카테고리일 때 지역 태그 자동 추가
    let finalData: any = { ...data };
    finalData.title = data.title.trim() || data.titleKo.trim();
    finalData.titleKo = data.titleKo.trim() || finalData.title;
    finalData.content = data.content.trim() || data.contentKo.trim();
    finalData.contentKo = data.contentKo.trim() || data.content.trim();
    finalData.summary = data.summary.trim() || data.summaryKo.trim();
    finalData.summaryKo = data.summaryKo.trim() || data.summary.trim();

    // Convert tags from string to array if needed
    if (typeof data.tags === "string") {
      const tagString = String(data.tags).trim();
      if (tagString) {
        finalData.tags = tagString
          .split(",")
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0);
      } else {
        finalData.tags = [];
      }
    }

    if (data.category === "community" && data.region && data.region !== "ALL") {
      const tags = Array.isArray(finalData.tags) ? finalData.tags : [];
      // region 태그가 없으면 추가
      if (!tags.includes(data.region)) {
        finalData.tags = [...tags, data.region];
      }
    }

    // Format publishedAt as ISO string if provided
    if (finalData.publishedAt) {
      const date = new Date(finalData.publishedAt);
      if (!isNaN(date.getTime())) {
        finalData.publishedAt = date.toISOString();
      }
    }

    if (isEditMode) {
      updateMutation.mutate(finalData);
    } else {
      createMutation.mutate(finalData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const mediaPending = imageUploadMutation.isPending || imageGenerateMutation.isPending;

  const saveAsDraft = () => {
    form.setValue("status", "draft", { shouldDirty: true });
    void form.handleSubmit(handleSubmit)();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link href="/admin/articles">
          <div>
            <Button
              variant="ghost"
              className="mb-6"
              data-testid="button-back-admin"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              대시보드로
            </Button>
          </div>
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {isEditMode ? "기사 스튜디오 · 수정" : "기사 스튜디오 · 새 기사"}
            </CardTitle>
            <CardDescription>
              직접 쓴 초안을 AI로 다듬고, 대표 이미지를 업로드하거나 생성한 뒤 초안·검토·발행 상태로 관리합니다.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-6"
              >
                <Card className="border-primary/20 bg-primary/[0.03]">
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <WandSparkles className="h-5 w-5 text-primary" /> AI 기사 편집 도우미
                        </CardTitle>
                        <CardDescription className="mt-1">
                          입력한 사실만 유지하며 문체와 구조를 정리합니다. AI 결과는 자동 발행되지 않습니다.
                        </CardDescription>
                      </div>
                      <Badge variant="outline" className="gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" /> 사람의 최종 검토 필수
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">편집 방식</label>
                        <Select value={aiMode} onValueChange={(value) => setAiMode(value as typeof aiMode)}>
                          <SelectTrigger data-testid="select-ai-edit-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="polish">전체 기사답게 다듬기</SelectItem>
                            <SelectItem value="structure">리드·문단 구조 정리</SelectItem>
                            <SelectItem value="headline">제목·요약 집중 개선</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <label className="text-sm font-medium">추가 요청사항</label>
                        <Input
                          value={aiInstructions}
                          onChange={(event) => setAiInstructions(event.target.value)}
                          placeholder="예: 시드니 교민 독자가 이해하기 쉽게, 과장 없이"
                          data-testid="input-ai-instructions"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">취재 메모·확인된 사실</label>
                      <Textarea
                        value={sourceNotes}
                        onChange={(event) => setSourceNotes(event.target.value)}
                        rows={3}
                        placeholder="공식 발표, 인터뷰 메모, 확인한 날짜·숫자·인용을 붙여 넣으세요."
                        data-testid="input-ai-source-notes"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        onClick={() => aiAssistMutation.mutate()}
                        disabled={aiAssistMutation.isPending}
                        data-testid="button-ai-polish"
                      >
                        {aiAssistMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                        AI로 기사답게 만들기
                      </Button>
                      <p className="text-xs text-muted-foreground">본문을 최소 40자 이상 작성한 뒤 사용하세요.</p>
                    </div>
                    {(factsToVerify.length > 0 || changeSummary.length > 0) && (
                      <div className="grid gap-3 rounded-lg border bg-background p-4 md:grid-cols-2">
                        <div>
                          <p className="mb-2 text-sm font-semibold text-amber-700">발행 전 사실 확인</p>
                          {factsToVerify.length ? (
                            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                              {factsToVerify.map((item) => <li key={item}>{item}</li>)}
                            </ul>
                          ) : <p className="text-sm text-muted-foreground">별도 확인 항목 없음</p>}
                        </div>
                        <div>
                          <p className="mb-2 text-sm font-semibold">AI가 정리한 내용</p>
                          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                            {changeSummary.map((item) => <li key={item}>{item}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>제목 (영문·선택)</FormLabel>
                        <FormControl>
                          <Input {...field} data-testid="input-title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="titleKo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>제목 (한글)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-title-ko"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>카테고리</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-category">
                              <SelectValue placeholder="카테고리 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="region"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>지역</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="select-region">
                              <SelectValue placeholder="지역 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {australianStates.map((state) => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.nameKo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          이 기사가 표시될 지역을 선택하세요
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="author"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>작성자</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-author"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>발행 상태</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-article-status">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">초안</SelectItem>
                            <SelectItem value="pending">검토 대기</SelectItem>
                            <SelectItem value="published">발행</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          발행은 출처·본문·이미지·크레딧 기준을 모두 통과해야 합니다.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="summary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>요약 (영문)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={3}
                          data-testid="input-summary"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="summaryKo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>요약 (한글)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value || ""}
                          rows={3}
                          data-testid="input-summary-ko"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="keyHighlight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>핵심 한줄 요약</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="3초 만에 파악할 수 있는 핵심 요약 1문장"
                          data-testid="input-key-highlight"
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        독자가 3초 만에 기사의 핵심을 파악할 수 있도록 하는 한
                        문장 요약입니다.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>본문 (영문)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          rows={10}
                          data-testid="input-content"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contentKo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>본문 (한글)</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value || ""}
                          rows={10}
                          data-testid="input-content-ko"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="imageUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이미지 URL</FormLabel>
                      <FormControl>
                        <div className="space-y-4">
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-image-url"
                          />
                          <Card className="bg-muted/30">
                            <CardContent className="space-y-4 pt-5">
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  ref={uploadInputRef}
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  className="hidden"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) imageUploadMutation.mutate(file);
                                    event.currentTarget.value = "";
                                  }}
                                  data-testid="input-article-image-file"
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={mediaPending}
                                  onClick={() => uploadInputRef.current?.click()}
                                  data-testid="button-upload-article-image"
                                >
                                  {imageUploadMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                                  내 이미지 업로드
                                </Button>
                                <Badge variant="secondary">JPG·PNG·WebP · 최대 8MB</Badge>
                              </div>
                              <div className="space-y-2 border-t pt-4">
                                <label className="flex items-center gap-2 text-sm font-medium">
                                  <ImagePlus className="h-4 w-4 text-primary" /> AI 대표 이미지 만들기
                                </label>
                                <Textarea
                                  value={imagePrompt}
                                  onChange={(event) => setImagePrompt(event.target.value)}
                                  rows={3}
                                  placeholder="예: 시드니 도심의 한인 소상공인이 매장 문을 여는 아침 풍경, 자연광, 뉴스 사진 스타일, 글자 없음"
                                  data-testid="input-image-prompt"
                                />
                                <div className="flex flex-wrap items-center gap-3">
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={mediaPending || imagePrompt.trim().length < 10}
                                    onClick={() => imageGenerateMutation.mutate()}
                                    data-testid="button-generate-article-image"
                                  >
                                    {imageGenerateMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                                    저비용 초안 이미지 생성
                                  </Button>
                                  <p className="text-xs text-muted-foreground">
                                    가로형 저품질 초안으로 생성합니다. 실제 사건의 증거 사진처럼 사용하지 마세요.
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                          {field.value && (
                            <div className="border rounded-md p-2">
                              <img
                                src={field.value}
                                alt="미리보기"
                                className="w-full h-48 object-cover rounded"
                                onError={(e) => {
                                  e.currentTarget.src =
                                    "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&h=600&fit=crop";
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        외부 URL은 사용 권한을 확인하세요. 업로드·AI 생성 시 크레딧이 자동 입력됩니다.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="imageDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이미지 대체 설명</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value || ""}
                          rows={2}
                          placeholder="예: A Korean family looking at house prices on laptop in modern Sydney apartment"
                          data-testid="input-image-description"
                        />
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        화면을 보지 못하는 독자도 이해할 수 있도록 이미지 내용을 설명하세요.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="sourceName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>출처명</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            placeholder="예: ABC News Australia"
                            data-testid="input-source-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="sourceUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>출처 URL</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-source-url"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="imageCredit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>이미지 출처·크레딧</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="예: ABC News / 사용 허가된 보도자료"
                          data-testid="input-image-credit"
                        />
                      </FormControl>
                      <FormDescription>
                        이미지 사용 권한을 확인할 수 있는 출처를 기록하세요.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>태그 (쉼표로 구분)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="예: 정치, 경제, 사회"
                          data-testid="input-tags"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isBreaking"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="checkbox-breaking"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>속보로 표시</FormLabel>
                        <FormDescription>
                          이 기사를 속보로 표시합니다. 속보 배지가 표시됩니다.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex flex-wrap gap-2 border-t pt-5">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={isPending}
                    onClick={saveAsDraft}
                    data-testid="button-save-draft"
                  >
                    <Save className="mr-2 h-4 w-4" /> 초안 저장
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending}
                    data-testid="button-create-article"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        저장 중...
                      </>
                    ) : (
                      "선택 상태로 저장"
                    )}
                  </Button>
                  <Link href="/admin/articles">
                    <div>
                      <Button type="button" variant="outline">
                        취소
                      </Button>
                    </div>
                  </Link>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
